import { useCallback, useEffect, useMemo } from 'react';
import { OnboardingProTask } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/onboarding.pb';
import { HOTSPOT_TARGET, useOnboardingShallowStore } from '@frontend/product-onboarding';
import { CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS, CI_PRO_ONBOARDING } from '../constant';
import { useProOnboardingActionShallowStore } from './use-pro-onboarding-action-store';

type UseProOnboardingFollowUpTasksResolverArgs = {
  enabled?: boolean;
  isLoading: boolean;
  schedulingTaskCount: number;
  totalFollowUpTaskCount: number;
  navigateToSchedulingTasks: () => void;
  navigateToAllFollowUps: () => void;
};

/**
 * Resolves PRO Task 5: Follow-up Tasks.
 *
 * Navigation rule:
 * - If Scheduling tasks exist, go to Scheduling task list.
 * - If Scheduling tasks are 0 but other follow-ups exist, go to All Follow-ups.
 * - If total tasks are 0, disable action and do not show list-button hotspot.
 */
export function useProOnboardingFollowUpTasksResolver({
  enabled = true,
  isLoading,
  schedulingTaskCount,
  totalFollowUpTaskCount,
  navigateToSchedulingTasks,
  navigateToAllFollowUps,
}: UseProOnboardingFollowUpTasksResolverArgs) {
  const { flows, completeHotspot, completedHotspots } = useOnboardingShallowStore(
    'flows',
    'completeHotspot',
    'completedHotspots'
  );

  const { pendingTask, clearPendingTask, setEligibility } = useProOnboardingActionShallowStore(
    'pendingTask',
    'clearPendingTask',
    'setEligibility'
  );

  const isProFlowRegistered = useMemo(() => flows.some((flow) => flow.id === CI_PRO_ONBOARDING.id), [flows]);

  const shouldResolveFollowUpTask = enabled && isProFlowRegistered;
  const hasFollowUpTasks = totalFollowUpTaskCount > 0;
  const hasSchedulingTasks = schedulingTaskCount > 0;

  const markHotspotComplete = useCallback(
    (hotspotKey: string) => {
      if (!completedHotspots.has(hotspotKey)) {
        completeHotspot(hotspotKey);
      }
    },
    [completedHotspots, completeHotspot]
  );

  const followUpListHotspotTarget = useMemo(() => {
    if (!shouldResolveFollowUpTask || isLoading || !hasFollowUpTasks) {
      return null;
    }

    if (hasSchedulingTasks) {
      const hasCompletedSchedulingListHotspot = completedHotspots.has(
        CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_SCHEDULING_TASKS_LIST_BUTTON
      );

      return hasCompletedSchedulingListHotspot
        ? null
        : HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_SCHEDULING_TASKS_LIST_BUTTON;
    }

    const hasCompletedAllFollowUpsListHotspot = completedHotspots.has(
      CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_ALL_FOLLOW_UPS_LIST_BUTTON
    );

    return hasCompletedAllFollowUpsListHotspot ? null : HOTSPOT_TARGET.CALL_INTEL_FOLLOW_UP_ALL_FOLLOW_UPS_LIST_BUTTON;
  }, [shouldResolveFollowUpTask, isLoading, hasFollowUpTasks, hasSchedulingTasks, completedHotspots]);

  /**
   * Resolve Task 5 arrow eligibility once follow-up counts are loaded.
   */
  useEffect(() => {
    if (!shouldResolveFollowUpTask) return;
    if (isLoading) return;

    setEligibility({
      hasEligibleFollowUpTasks: totalFollowUpTaskCount > 0,
    });
  }, [shouldResolveFollowUpTask, isLoading, totalFollowUpTaskCount, setEligibility]);

  useEffect(() => {
    if (!shouldResolveFollowUpTask) return;
    if (pendingTask !== OnboardingProTask.PRO_TASK_FOLLOW_UP_TASKS) return;
    if (isLoading) return;

    /**
     * No tasks at all.
     * Disable future arrow click and clear pending state.
     */
    if (totalFollowUpTaskCount <= 0) {
      setEligibility({
        hasEligibleFollowUpTasks: false,
      });

      clearPendingTask();
      return;
    }

    setEligibility({
      hasEligibleFollowUpTasks: true,
    });

    markHotspotComplete(CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_TASKS_TAB);

    if (schedulingTaskCount > 0) {
      markHotspotComplete(CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_SCHEDULING_TASKS_LIST_BUTTON);
      clearPendingTask();
      navigateToSchedulingTasks();
      return;
    }

    /**
     * Scheduling count is 0 but other follow-ups exist.
     * Fall back to All Follow-ups.
     */
    markHotspotComplete(CALL_INTEL_PRO_ONBOARDING_HOTSPOT_KEYS.FOLLOW_UP_ALL_FOLLOW_UPS_LIST_BUTTON);
    clearPendingTask();
    navigateToAllFollowUps();
  }, [
    shouldResolveFollowUpTask,
    pendingTask,
    isLoading,
    totalFollowUpTaskCount,
    schedulingTaskCount,
    setEligibility,
    clearPendingTask,
    markHotspotComplete,
    navigateToSchedulingTasks,
    navigateToAllFollowUps,
  ]);

  return {
    followUpListHotspotTarget,
  };
}
